import { getStockTrackingValue } from '../composables/stock-status.composable';
import Cart from '../models/cart.model';
import Order from '../models/order.model';
import Vehicle from '../models/vehicle.model';
import { TrackingCartData, TrackingLinkClickEvent, TrackingProductData, TrackingShipMethod } from './tracking.types';
import {
  getOrderPricewtax,
  getOrderProductPaymethod,
  getOrderProductStockMapping,
  numberOfProductsInOrder,
  orderEntriesFlattened,
} from '../../../confirmation/src/utils/order';
import { isLegalFormCorporation, isLegalFormPartnership } from '../utils/legal-form.utils';
import { SMSEnumDTO, SMSOrderEntry } from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import { getProductDetailParam, ProductDetail } from '@dh-io-emh/commerce-api/src/models/product';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';

const getProductId = (baumuster: string, code: string) => (baumuster?.length > 0 ? baumuster.slice(0, 4) : code);

const getProductVariantId = (baumuster: string) => (baumuster?.length > 0 ? baumuster.slice(0, 6) : undefined);

const formatValue = (value?: string) => (value ? value.toLowerCase() : value);

const getShipMethod = (cart: Cart) =>
  cart.deliveryMode?.code
    ? ({ homedelivery: 'home:standard', pickup: 'pick_up:standard' }[cart.deliveryMode.code] as TrackingShipMethod)
    : undefined;

export interface ProductDataTrackingObjectParams {
  product: ProductDetail | Vehicle;
  index: number;
  price?: number;
  qty?: number;
}

export const getProductDataTrackingObject = ({
  product,
  index,
  price,
  qty,
}: ProductDataTrackingObjectParams): TrackingProductData[] => {
  return [
    {
      id: getProductId(
        getProductDetailParam(product, 'baumuster') as string,
        getProductDetailParam(product, 'code') as string,
      ),
      variantId: getProductVariantId(getProductDetailParam(product, 'baumuster')!),
      ...(index > -1 && { index }), // add index if index > -1 otherwise omit
      ...(qty && { qty }), // add quantity if given otherwise omit
      articleId: getProductDetailParam(product, 'code')!,
      pricewtax: price ? price : product.price?.value,
      retailerId: product.pointOfServiceCode,
      type: getProductDetailParam(product, 'productType')!,
      bodyType: formatValue(getProductDetailParam(product, 'bodyType')?.code),
      class: undefined,
      fuelType: formatValue(getProductDetailParam(product, 'fuelType')?.code),
      transmission: formatValue(getProductDetailParam(product, 'transmission')),
      brand: formatValue(getProductDetailParam(product, 'brand')),
      inStock: getStockTrackingValue(product.stock),
    },
  ];
};

export const getProductsDataTrackingObject = (products: Vehicle[], startIndex = 0): TrackingProductData[] => {
  return products.map((product, index) => ({
    id: getProductId(product?.baumuster as string, product?.code as string),
    variantId: getProductVariantId(product?.baumuster as string),
    articleId: product.code,
    index: startIndex + index,
    retailerId: product.pointOfServiceCode,
    type: product.productType || '',
    bodyType: formatValue(product.bodyType?.code),
    class: undefined,
    fuelType: formatValue(product.fuelType?.code),
    transmission: formatValue(product.gearBox?.code),
    brand: formatValue(product.brand),
    inStock: getStockTrackingValue(product.stock),
  }));
};

export const getCartDataTrackingObject = (
  cart: Cart,
  product: ProductDetail,
  totalPrice?: number,
): TrackingCartData => {
  return {
    id: cart.code,
    qty: cart.partialOrderList.length,
    pricewtax: cart.totalPriceWithTax?.value,
    products: [
      {
        ...getProductDataTrackingObject({
          product,
          index: -1,
          qty: 1,
          price: totalPrice,
        })[0],
        tax: product.price?.priceDetail?.taxValue,
        paymethod: cart.paymentMode?.code,
        shipmethod: getShipMethod(cart),
        retailerId: cart.pickupPointOfService?.name,
      },
    ],
  };
};

export const getTransactionDataTrackingObject = (order: Order) => ({
  id: order.code,
  qty: numberOfProductsInOrder(order.partialOrderList),
  pricewtax: getOrderPricewtax(order.partialOrderList),
  shipcost: order.deliveryCost?.value,
  tax: undefined, // 'tax' currently not provided in BE API
  products: orderEntriesFlattened(order.partialOrderList).map((entry: SMSOrderEntry) => ({
    id: getProductId(entry.product?.baumuster as string, entry.product?.code as string),
    name: entry.product?.name?.toLowerCase().replaceAll(' ', '_'),
    variantId: getProductVariantId(entry.product?.baumuster as string),
    articleId: entry.product?.code ?? '',
    qty: entry.quantity,
    pricewtax: entry.product?.discountPrice?.value,
    tax: entry.product?.price?.priceDetail?.taxValue, // 'tax' currently not provided in BE API
    paymethod: getOrderProductPaymethod(order),
    shipmethod: getShipMethod(entry as Cart) ?? 'no_shipment',
    retailerId: entry.product?.pointOfServiceCode || entry.deliveryPointOfService?.name || '',
    shipcost: order.deliveryCost?.value,
    type: entry.product?.dcpProductType?.name ?? '',
    inStock: getOrderProductStockMapping(entry.product),
    bodyType: entry.product?.bodyType?.code?.toLowerCase(),
    brand: entry.product?.brand?.toLowerCase(),
    class: undefined, // excplicitly requested this way, to be set in the near future
    fuelType: entry.product?.fuelType?.code?.toLowerCase(),
    transmission: entry.product?.gearBox?.code?.toLowerCase(),
  })),
});

export const getDownstreamPaymentModeMapping = (paymentMode) => {
  switch (paymentMode) {
    case 'FINANCIAL_SERVICE':
      return 'financing';
    case 'DIRECT_PAYMENT':
      return 'cash';
    default:
      return 'other';
  }
};

export const getLegalTypeMapping = (legalForm) => {
  if (isLegalFormPartnership(legalForm)) return 'partnership';

  if (isLegalFormCorporation(legalForm)) return 'corporation';
  else return 'selfEmployed';
};

export const getLegalTypeToSnakeCase = (legalType) => {
  let toLowerCaseLegalType = legalType.toLowerCase();

  toLowerCaseLegalType = toLowerCaseLegalType.replace(/ /g, '_');

  return toLowerCaseLegalType;
};

export const getClickDataTrackingObject = (eventName: string, payload?: unknown): TrackingLinkClickEvent => {
  const { checkout: checkoutConfig } = infuse('config');
  const enablePaymentMethodStep = checkoutConfig.enablePaymentMethodStep;

  const getStepNumber = (defaultStepNumber: number): string => {
    return `step_${enablePaymentMethodStep ? defaultStepNumber + 1 : defaultStepNumber}`;
  };

  const events = {
    loginRegisterStepImpression: {
      eventDetail: {
        action: 'checkout_step.login_register',
        category: 'impression',
        label: 'step_1',
      },
    },
    personalDataImpression: {
      eventDetail: {
        action: 'checkout_step.personal_data',
        category: 'impression',
        label: 'step_2',
      },
    },
    businessDataImpression: {
      eventDetail: {
        action: 'checkout_step.business_data',
        category: 'impression',
        label: 'step_2',
      },
    },
    paymentOptionsImpression: {
      eventDetail: {
        action: 'checkout_step.payment_options',
        category: 'impression',
        label: 'step_3',
      },
    },
    paymentOptionsFeature: {
      eventDetail: {
        action: 'checkout_step.payment_options',
        category: 'feature',
        label: payload,
      },
    },
    paymentMethodImpression: {
      eventDetail: {
        action: 'checkout_step.payment_method',
        category: 'impression',
        label: 'step_4',
      },
    },
    paymentMethodFeature: {
      eventDetail: {
        action: 'checkout_step.payment_method',
        category: 'feature',
        label: payload,
      },
    },
    deliveryOptionsImpression: {
      eventDetail: {
        action: 'checkout_step.delivery_options',
        category: 'impression',
        label: getStepNumber(4),
      },
    },
    orderOverviewImpression: {
      eventDetail: {
        action: 'checkout_step.order_overview',
        category: 'impression',
        label: getStepNumber(5),
      },
    },

    orderConfirmationCtaFinanceFeature: {
      eventDetail: {
        category: 'feature',
        action: 'order_confirmation',
        label: 'cta.finance',
      },
    },
    orderConfirmationCtaOneDocFeature: {
      eventDetail: {
        category: 'feature',
        action: 'order_confirmation',
        label: 'cta.oneDoc',
      },
    },
    personalDataFormStarted: {
      eventDetail: {
        action: 'checkout_step.personal_data',
        category: 'form',
        label: 'form_started',
      },
    },
    personalDataFormSubmitted: {
      eventDetail: {
        action: 'checkout_step.personal_data',
        category: 'form',
        label: 'form_submitted',
      },
    },
    businessDataFormStarted: {
      eventDetail: {
        action: 'checkout_step.business_data',
        category: 'form',
        label: 'form_started',
      },
    },
    businessDataFormSubmitted: {
      eventDetail: {
        action: 'checkout_step.business_data',
        category: 'form',
        label: 'form_submitted',
      },
    },
    corporationTypeOptionsFeature: {
      eventDetail: {
        action: 'corporation_type_options',
        category: 'feature',
        label: payload,
      },
    },
    deliveryOptionsFeature: {
      eventDetail: {
        action: 'checkout_step.delivery_options',
        category: 'feature',
        label: payload,
      },
    },
    orderOverviewFeature: {
      eventDetail: {
        action: 'checkout_step.order_overview',
        category: 'feature',
        label: 'proceed',
      },
    },
  };

  return events[eventName];
};

export const getParseBodyTypeCode = (bodyType: SMSEnumDTO) =>
  bodyType.code?.toLowerCase().replace(' ', '_').replace('-', '_') ?? 'unknown';
